import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { colorStringForShort, CustomisationKey } from "@scenes/pdp/types";
import { FilterStructure } from "@scenes/searchResults/types";
import { Diamond } from "@src/types/DiamondDetail";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import { CollectionMenuItem } from "@src/types/models";
import { GlobalEnum, ProductCategory } from "@utils/constants";
import {
  formatNumberWithPrecision,
  percent,
} from "@utils/product/productFormatters";
import {
  getTwoProductProperty,
  itemLocation,
} from "@utils/product/productProperties";
import { isLooseParcel, isMeleeItem } from "@utils/product/productValidators";
import { SearchResultsAndFavoritesEnum } from "@utils/product/searchResultConstants";
import currency from "currency.js";

export type RequestDetailTabletProps = {
  mItem?: Diamond;
  menuOptions: Array<CollectionMenuItem>;
  menuOptionSelected?: any;
  checkBoxSelection: any;
  ismItemChecked: boolean;
  productType: ProductCategory;
  handleItemClick?: any;
  favoriteClickedFunction?: any;
  index?: number;
  removeFavorite?: boolean;
  priceMode?: any;
  customizeColumnParams?: FilterStructure[];
  userCurrency?: any;
  productPrefix?: string;
  fromRequestDetail?: boolean;
  stockItemRequest?: StockItemRequestClass;
  stockItemRequestParent?: StockItemRequestClass;
  showListPrice?: boolean;
  isTotalOfferDiscountApplied?: boolean;
  request_type?: string;
  hasAnyOfferDetails?: boolean;
  viewType?: string;
  sectionName?: SearchFilterTypeEnum;
};

export const formatItemValue = (
  isCustomApp: boolean,
  key: string,
  item?: Diamond,
  userCurrency?: string,
  userInfo?: any,
  translation?: any,
  isShortValue?: boolean,
  stockItemRequest?: StockItemRequestClass
) => {
  switch (key) {
    case "color":
      return colorStringForShort(isShortValue ? isShortValue : false, item);
    case SearchResultsAndFavoritesEnum.HEARTS_AND_ARROWS:
      return translation &&
        item?.hearts_and_arrows ===
          SearchResultsAndFavoritesEnum.HEARTS_AND_ARROWS_VALUE
        ? translation("global.text.yes")
        : translation("global.text.no");
    case "on_hand":
      switch (item?.on_hand) {
        case GlobalEnum.ON_HAND:
          return userInfo?.onHandText(GlobalEnum.ON_HAND);
        case GlobalEnum.PLEASE_EMAIL:
          return userInfo?.onHandText(GlobalEnum.PLEASE_EMAIL);
        default:
          return userInfo?.onHandText(GlobalEnum.OUT_ON_MEMO);
      }
    case "available":
      switch (item?.on_hand) {
        case GlobalEnum.ON_HAND:
          return userInfo?.onHandText(GlobalEnum.ON_HAND);
        case GlobalEnum.PLEASE_EMAIL:
          return userInfo?.onHandText(GlobalEnum.PLEASE_EMAIL);
        default:
          return userInfo?.onHandText(GlobalEnum.OUT_ON_MEMO);
      }
    case "total_sales_price":
      return isCustomApp && isLooseParcel(item) && item?.hide_total_sales_price
        ? "-"
        : item?.total_sales_price
        ? parseFloat(item.total_sales_price) === 0
          ? "-"
          : currency(item.total_sales_price, {
              symbol: userCurrency,
              precision: isCustomApp
                ? userInfo?.roundUpPrices() && !isMeleeItem(item)
                  ? 0
                  : 2
                : 0,
            }).format()
        : "-";

    case "offer_total_sales_price":
      return item?.offer_total_sales_price
        ? parseFloat(item.offer_total_sales_price) === 0
          ? "-"
          : currency(item.offer_total_sales_price, {
              symbol: userCurrency,
              precision: isCustomApp
                ? userInfo?.roundUpPrices() && !isMeleeItem(item)
                  ? 0
                  : 2
                : 0,
            }).format()
        : "-";
    case "retail_price":
      return item?.retail_price
        ? currency(item.retail_price, {
            symbol: userCurrency,
            precision: userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
          }).format()
        : "-";
    case CustomisationKey.price_per_carat:
      return isCustomApp && isLooseParcel(item) && item?.hide_price_per_carat
        ? "-"
        : item?.price_per_carat
        ? parseFloat(item.price_per_carat) === 0
          ? "-"
          : currency(item.price_per_carat, {
              symbol: userCurrency,
              precision: 0,
            }).format()
        : "-";
    case CustomisationKey.offer_price_per_carat:
      return item?.offer_price_per_carat
        ? parseFloat(item.offer_price_per_carat) === 0
          ? "-"
          : currency(item.offer_price_per_carat, {
              symbol: userCurrency,
              precision: 0,
            }).format()
        : "-";
    case CustomisationKey.discount_percent:
      return percent(item?.discount_percent);

    case "locations":
      return item?.locations ? itemLocation(item) : "-";
    case "depth_percent":
      return percent(item?.depth_percent);
    case "offer_discount_percent":
      return percent(item?.offer_discount_percent);

    case "table_percent":
      return percent(item?.table_percent);

    case "crown_height":
      return getTwoProductProperty(
        item?.crown_height ?? "",
        "%",
        item?.crown_angle ?? "",
        "°"
      );

    case "pavilion_depth":
      return getTwoProductProperty(
        item?.pavilion_depth ?? "",
        "%",
        item?.pavilion_angle ?? "",
        "°"
      );

    case "rapnet_price":
      return item?.rapnet_price
        ? currency(item.rapnet_price, {
            symbol: userCurrency,
            precision: userInfo?.roundUpPrices() && !isMeleeItem(item) ? 0 : 2,
          }).format()
        : "-";
    case "stock_num":
      return item?.stock_num
        ? (isCustomApp ? "" : "VDB-") + item?.stock_num
        : "-";
    case "size":
      return item?.size
        ? parseFloat(item?.size) === 0
          ? "-"
          : item.size
        : "-";
    case "status":
      return stockItemRequest?.status;
    case "parcel_stones":
      return item?.parcel_stones
        ? parseFloat(item?.parcel_stones) === 0
          ? "-"
          : item?.parcel_stones
        : "-";
    case "quantity":
      return item?.quantity
        ? parseFloat(item?.quantity) === 0
          ? "-"
          : item?.quantity
        : "-";
    case "offer_size":
      return item?.offer_size
        ? parseFloat(item?.offer_size) === 0
          ? "-"
          : item?.offer_size
        : "-";
    case "meas_length":
      return item?.meas_length
        ? parseFloat(item?.meas_length) === 0
          ? "-"
          : formatNumberWithPrecision(item?.meas_length, { precision: 2 })
        : "-";
    case "total_amount":
      return item?.total_amount
        ? currency(item.total_amount, {
            symbol: userCurrency,
            precision: userInfo?.roundUpPrices() ? 0 : 2,
          }).format()
        : "-";
    default:
      return item?.[key] || "-";
  }
};
